import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Toast } from "primereact/toast";
import { useTranslation } from "react-i18next";
import { DateInput } from "../../components/DateInput/DateInput";
import { ProgressSpinner } from "primereact/progressspinner";
import { AuditLogListItem } from "../../models/AuditLog";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { getAuditLogs } from "../../services/NewVersionService";
import { Utility } from "../../core/Utility";
import "./AuditLogPage.scss";

export const AuditLogPage = () => {
  const { t } = useTranslation();
  const toast = useRef(null);

  const today = new Date();
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const todayEnd = () => endOfDay(new Date());

  // inicijalni opseg – 30 dana unazad
  const initialDateFrom = startOfDay(
    new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30)
  );

  const initialDateTo = endOfDay(today);

  const [dateFrom, setDateFrom] = useState<Date>(initialDateFrom);
  const [dateTo, setDateTo] = useState<Date>(initialDateTo);
  const [processing, setProcessing] = useState(false);
  const [items, setItems] = useState<AuditLogListItem[]>([]);

  const [filters, setFilters] = useState({
    userName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    actionLabel: { value: null, matchMode: FilterMatchMode.CONTAINS },
    entityLabel: { value: null, matchMode: FilterMatchMode.CONTAINS },
    entityId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    note: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const ACTION_LABEL: Record<string, string> = {
    DELETE: t("audit.action.DELETE", "Brisanje"),
    UNLOCK: t("audit.action.UNLOCK", "Otključavanje"),
  };

  const ENTITY_LABEL: Record<string, string> = {
    PRODUCT_INPUT: t("audit.entity.PRODUCT_INPUT", "Prijemnica"),
    PRODUCT_OUTPUT: t("audit.entity.PRODUCT_OUTPUT", "Otpremnica"),
    COMMERCIAL_REQUEST: t("audit.entity.COMMERCIAL_REQUEST", "Zahtev"),
    DISPOSITION: t("audit.entity.DISPOSITION", "Dispozicija"),
    PRODUCT: t("audit.entity.PRODUCT", "Roba"),
    OWNERSHIP_SHEET_MERCANTILE: t(
      "audit.entity.OWNERSHIP_SHEET_MERCANTILE",
      "Vlasnički list (merkantila)"
    ),
    OWNERSHIP_SHEET_RAW_MATERIAL: t(
      "audit.entity.OWNERSHIP_SHEET_RAW_MATERIAL",
      "Vlasnički list (repromaterijal)"
    ),
    PROCESSING_ORDER: t("audit.entity.PROCESSING_ORDER", "Nalog za doradu"),
    PROCESSING_LOG: t("audit.entity.PROCESSING_LOG", "Zapisnik o doradi"),
    DECLARATION: t("audit.entity.DECLARATION", "Deklaracija"),
  };

  const load = (from: Date, to: Date) => {
    setProcessing(true);
    getAuditLogs(from, to)
      .then((res: AxiosResponse) => {
        const mapped = (res.data.result ?? []).map((x: any) => ({
          ...x,
          actionLabel: ACTION_LABEL[x.action] ?? x.action ?? "",
          entityLabel: ENTITY_LABEL[x.entity] ?? x.entity ?? "",
        }));

        setItems(mapped);
      })
      .catch((e) => {
        console.error(e); //@ts-ignore
        toast.current?.show?.({
          severity: "error",
          detail: t("Greška pri učitavanju audit logova"),
          life: 3000,
        });
      })
      .finally(() => setProcessing(false));
  };

  useEffect(() => {
    if (dateFrom && dateTo) load(dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDateFromChange = (_: string, value: Date | null) => {
    if (!value) return;

    const normalizedFrom = startOfDay(value);
    let normalizedTo = dateTo;
    if (normalizedFrom > dateTo) {
      normalizedTo = endOfDay(value);
      setDateTo(normalizedTo);
    }
    setDateFrom(normalizedFrom);
    load(normalizedFrom, normalizedTo);
  };

  const onDateToChange = (_: string, value: Date | null) => {
    if (!value) return;

    let normalizedTo = endOfDay(value);
    const maxToday = todayEnd();
    if (normalizedTo > maxToday) {
      normalizedTo = maxToday;
    }
    let normalizedFrom = dateFrom;
    if (normalizedTo < dateFrom) {
      normalizedFrom = startOfDay(value);
      setDateFrom(normalizedFrom);
    }
    setDateTo(normalizedTo);
    load(normalizedFrom, normalizedTo);
  };

  const formatCreatedAt = (row: AuditLogListItem) => {
    if (!row?.createdAt) return "";
    return Utility.formatDate(new Date(row.createdAt as any));
  };

  const formatTimeCreatedAt = (row: AuditLogListItem) => {
    if (!row?.createdAt) return "";
    return Utility.formatTime(new Date(row.createdAt as any));
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-journal-text header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
                transform: "translate(0, 2px)",
              }}
            ></i>
            <span className="header-left-title">
              {t("Istorija aktivnosti u sistemu")}
            </span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              maxDate={dateTo}
              onValueChange={onDateFromChange}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              minDate={dateFrom}
              maxDate={new Date()}
              onValueChange={onDateToChange}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right"></div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              emptyMessage="Nema logova za izabrani period"
              value={items}
              showGridlines
              stripedRows
            >
              <Column
                body={formatCreatedAt}
                header={t("Datum")}
                style={{ minWidth: "120px" }}
              />
              <Column
                body={formatTimeCreatedAt}
                header={t("Vreme")}
                style={{ minWidth: "120px" }}
              />
              <Column
                field="userName"
                filter
                header={t("Korisnik")}
                style={{ minWidth: "160px" }}
              />
              <Column
                field="actionLabel"
                filter
                header={t("Akcija")}
                style={{ minWidth: "120px" }}
              />
              <Column
                field="entityLabel"
                filter
                header={t("Entitet")}
                style={{ minWidth: "200px" }}
              />
              {/* <Column
                field="entityId"
                filter
                header={t("Entity ID")}
                style={{ minWidth: "240px" }}
              /> */}
              <Column
                field="note"
                filter
                header={t("Info")}
                style={{ minWidth: "300px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
